import { differenceInDays, differenceInMinutes } from 'date-fns';
import { isEmpty, isNil, isNumber } from 'lodash';
import {
  Classifications,
  Episode,
  FilteredUpcomingTile,
  LaunchType,
  Playable,
  Show,
} from '../types/launch';

export enum LaunchCode {
  CABLE = 'cutthecord',
  CHANNELS = 'channels',
  COMFORT = 'comfort',
  COMPETITOR = 'competition',
  DEVICES = 'devices',
  EPIX = 'epix',
  FAQ = 'faq',
  FAST = 'free-channels',
  FEATURES = 'getstarted',
  INSTALL = 'install',
  JSFV = 'jsfv6',
  MGM = 'mgmplus',
  MOVIES = 'moviesandmore',
  NO_SPORTS_BBALL = 'nosports-bball',
  NO_SPORTS_FBALL = 'nosports-fball',
  NO_SPORTS_COUPLE = 'nosports-couple',
  RPDR = 'rpdr15',
  SISTAS = 'sistas5',
  STARZ = 'starz',
  SYTTD = 'sayyestothedress',
  TMO = 'tmobile',
  YELLOWSTONE = 'yellowstone5',
  WELCOME = 'welcome',
  // genres
  COMEDY = 'comedyseries',
  CRITICALLY_ACCLAIMED = 'criticallyacclaimed',
  DRAMA = 'dramaticseries',
  HIPHOP = '50yearsofhiphop',
  REALITY = 'realityroundup',
  ROMANTIC_COMEDY = 'romanticcomedies',
  SITCOMS = 'sitcoms',
  THROWBACK = 'throwbacks',
  TRUE_CRIME = 'truecrime',
}

export const FALLBACK_TILE_THUMBNAIL = '/storage/images/default/show-default-m.jpg';

const YELLOWSTONE_PROMO_DIR = '/storage/images/promo/yellowstone-lp-assets';
const YELLOWSTONE_PROMO_IMAGE_PFX = `${YELLOWSTONE_PROMO_DIR}/yellowstone_s5_hero`;
const SISTAS_PROMO_DIR = '/storage/images/promo/sistas-lp-assets';
const SISTAS_PROMO_IMAGE_PFX = `${SISTAS_PROMO_DIR}/sistas_s5_hero`;
const RPDR_PROMO_DIR = '/storage/images/promo/rpdr-lp-assets';
const RPDR_PROMO_IMAGE_PFX = `${RPDR_PROMO_DIR}/rpdr_s15_hero`;
const JSFV_PROMO_DIR = '/storage/images/promo/jsfv-lp-assets';
const JSFV_PROMO_IMAGE_PFX = `${JSFV_PROMO_DIR}/jsfv_s6_hero`;

// TODO: KU – explore leveraging Contentful to automate image selection
export const PromoImages: Record<string, Record<'mobile' | 'desktop', string>> = {
  [LaunchCode.YELLOWSTONE]: {
    mobile: `${YELLOWSTONE_PROMO_IMAGE_PFX}_mobile.jpg`,
    desktop: `${YELLOWSTONE_PROMO_IMAGE_PFX}.jpg`,
  },
  [LaunchCode.SISTAS]: {
    mobile: `${SISTAS_PROMO_IMAGE_PFX}_mobile.jpg`,
    desktop: `${SISTAS_PROMO_IMAGE_PFX}.jpg`,
  },
  [LaunchCode.RPDR]: {
    mobile: `${RPDR_PROMO_IMAGE_PFX}_mobile.jpg`,
    desktop: `${RPDR_PROMO_IMAGE_PFX}.jpg`,
  },
  [LaunchCode.JSFV]: {
    mobile: `${JSFV_PROMO_IMAGE_PFX}_mobile.jpg`,
    desktop: `${JSFV_PROMO_IMAGE_PFX}_v3.jpg`,
  },
};

// TODO: KU – explore leveraging Contentful to automate image selection
export const PromoLogos: Record<string, string> = {
  [LaunchCode.YELLOWSTONE]: `${YELLOWSTONE_PROMO_DIR}/yellowstone_logo.png`,
  [LaunchCode.SISTAS]: `${SISTAS_PROMO_DIR}/sistas_logo.svg`,
  'Paramount Network': `${YELLOWSTONE_PROMO_DIR}/ParamountNetwork.svg`,
  [LaunchCode.RPDR]: `${RPDR_PROMO_DIR}/rpdr_logo.png`,
  [LaunchCode.JSFV]: `${JSFV_PROMO_DIR}/jsfv_logo.svg`,
};

// TODO: KU – explore leveraging Contentful to automate trailer selection
export const Trailers: Record<string, string> = {
  [LaunchCode.YELLOWSTONE]: `${YELLOWSTONE_PROMO_DIR}/yellowstone-S5-trailer.mp4`,
  [LaunchCode.SISTAS]: `${SISTAS_PROMO_DIR}/sistas-S5B-trailer.mp4`,
  [LaunchCode.RPDR]: `${RPDR_PROMO_DIR}/rpdr-S15-trailer.mp4`,
  [LaunchCode.JSFV]: `${JSFV_PROMO_DIR}/jsfv-s6-trailer.mp4`,
};

export const TMO_HUB_PROMO = 'hub';

export const IGNORED_SHOWS = ['Paid Programming', 'Sign Off'];

export const LAUNCH_CODE_PACKAGE_ALIASES: Record<string, string> = {
  [LaunchCode.MGM]: LaunchCode.EPIX,
  [LaunchCode.MOVIES]: 'movie',
  [LaunchCode.TMO]: TMO_HUB_PROMO,
};

export const SHOW_PAGE_LAUNCH_CODE_TYPES = new Set([
  LaunchType.SHOW,
  LaunchType.SEASON,
  LaunchType.EPISODE,
]);

export const PER_MONTH_SUFFIX = '/mo';
export const PER_MONTH_SUFFIX_LONG = '/month';

export const LAUNCH_CTA_TEST_ID = 'start-trial-button';
export const LAUNCH_UNCREDENTIALED_CTA_TEST_ID = 'watch-free-button';

export const SUBTITLE_DEFAULT = 'Watch now';
export const SUBTITLE_COMING_SOON = 'Coming Soon';
export const SUBTITLE_NOT_AVAILABLE = 'Not available';

export function getCTAText(isUserLoggedIn: boolean) {
  return isUserLoggedIn ? 'Watch now' : 'Start free trial';
}

export function getTileLabelPrefix(isUserLoggedIn: boolean) {
  return isUserLoggedIn ? '' : 'Sign up to';
}

export function getPlayableKey({ id, startsAt }: Playable) {
  return `${id}-${startsAt}`;
}

export function isValidShow({ title }: { title?: string } = {}) {
  return !IGNORED_SHOWS.some(it => title?.match(new RegExp(it, 'i')));
}

export function isValidPlayable({ show }: Playable) {
  return isValidShow(show);
}

export function filterEpisodeTiles<T>(
  assets: Playable[],
  startingAfter?: T,
): (T extends Date ? FilteredUpcomingTile : Playable)[] {
  const tileIds = new Set();
  const filteredTiles = assets.filter(
    (asset): asset is T extends Date ? FilteredUpcomingTile : Playable => {
      const { startsAt = '' } = asset;
      if (!isNil(startingAfter) && isEmpty(startsAt)) {
        return false;
      }
      const key = getPlayableKey(asset);
      const hasKey = tileIds.has(key);
      if (!hasKey) {
        tileIds.add(key);
      }
      if (isNil(startingAfter)) {
        return !hasKey && isValidPlayable(asset);
      }
      const parsedDate = Date.parse(startsAt);
      const isUpcoming =
        !isNil(startingAfter) && isNumber(parsedDate) && parsedDate > +startingAfter;
      return !hasKey && isValidPlayable(asset) && isUpcoming;
    },
  );
  return filteredTiles;
}

export function filterAndSortSeasonEpisodeTiles(assets: Playable[]) {
  const filteredTiles = filterEpisodeTiles(assets);
  return filteredTiles.sort((a, b) => {
    const { episodeNum: episodeA, title: titleA = '' } = a;
    const { episodeNum: episodeB, title: titleB = '' } = b;

    if (!isNumber(episodeA) && !isNumber(episodeB)) {
      return !titleA || (titleB && titleA.toUpperCase() > titleB.toUpperCase()) ? 1 : -1;
    }
    if (!isNumber(episodeA)) {
      return 1;
    }
    if (!isNumber(episodeB)) {
      return -1;
    }
    return episodeA - episodeB;
  });
}

export function filterAndSortUpcomingTiles(assets: Playable[], date = new Date()) {
  const filteredTiles = filterEpisodeTiles(assets, date);
  return filteredTiles.sort((a, b) => {
    return +new Date(a.startsAt) - +new Date(b.startsAt);
  });
}

export function isLivePlayable({ endsAt = '', startsAt = '' }: Playable) {
  return Date.parse(startsAt) < Date.now() && Date.parse(endsAt) > Date.now();
}

export function getEpisodeImageURL(episode: Episode | null, show?: Show) {
  return episode?.iconic || show?.iconic || show?.thumb || '';
}

export function getA11yPerMonthLabel(text: string, suffix = PER_MONTH_SUFFIX) {
  return text.replace(suffix, ' per month');
}

export function trimShowCast(cast: Show['cast'], max = Infinity) {
  return cast?.slice(0, max).join(', ').replace(/\s+,/g, ',');
}

export function getShowRatingsAdvisories(ratingsAdvisories: Show['ratings']) {
  const map: Record<'ratings' | 'advisories', string[]> = {
    ratings: [],
    advisories: [],
  };

  ratingsAdvisories?.forEach(({ classification, value }) => {
    if (Classifications.RATING.includes(classification)) {
      map.ratings.push(value);
    } else if (Classifications.ADVISORY.includes(classification)) {
      map.advisories.push(value);
    }
  });

  return map;
}

export function isNewUpcomingPlayable(upcoming: Playable) {
  const now = Date.now();
  return (
    !!upcoming.originalAirDate &&
    differenceInDays(upcoming.originalAirDate, now) >= 0 &&
    upcoming.startsAt &&
    differenceInMinutes(upcoming.startsAt, now) > 0
  );
}
